import { type Edge, type Node } from '@xyflow/react';
import { type BaseNodeData } from '../types';
import type { AutomationStepNodeData } from '../types/stepNodes';
import { FLOW_VERTICAL_SPACING } from './layoutConstants';

interface NodeDimensions {
	width: number;
	height: number;
}

/**
 * Type guard to check if node data has isExpanded property
 */
function hasIsExpandedProperty(data: BaseNodeData): data is AutomationStepNodeData {
	return 'isExpanded' in data;
}

/**
 * Estimates node dimensions based on whether it's expanded or not
 */
export function estimateNodeDimensions(node: Node<BaseNodeData>): NodeDimensions {
	const isExpanded = hasIsExpandedProperty(node.data) ? node.data.isExpanded : false;
	const estimatedHeight = hasIsExpandedProperty(node.data) ? node.data.estimatedHeight : undefined;

	// Expanded nodes are sized based on their content
	if (isExpanded) {
		const height = typeof estimatedHeight === 'number' ? estimatedHeight : 300;
		return {
			width: 420,
			height,
		};
	}

	// Compact nodes have a standard size
	return {
		width: 420,
		height: 100,
	};
}

/**
 * Finds all nodes that need to be repositioned when a node expands/collapses
 */
export function findAffectedNodes(
	changedNodeId: string,
	allNodes: Node<BaseNodeData>[],
	allEdges: Edge[]
): Node<BaseNodeData>[] {
	const affectedNodeIds = new Set<string>();
	const visited = new Set<string>();

	// Start with the changed node
	const queue = [changedNodeId];
	visited.add(changedNodeId);

	// Find all downstream nodes (nodes that come after this one in the flow)
	while (queue.length > 0) {
		const currentNodeId = queue.shift()!;

		// Find all edges starting from this node
		const outgoingEdges = allEdges.filter(edge => edge.source === currentNodeId);

		for (const edge of outgoingEdges) {
			if (!visited.has(edge.target)) {
				visited.add(edge.target);
				affectedNodeIds.add(edge.target);
				queue.push(edge.target);
			}
		}
	}

	return allNodes.filter(node => affectedNodeIds.has(node.id));
}

/**
 * Calculates the new position for affected nodes based on the changed node's new dimensions
 */
export function calculateAdjustedPositions(
	changedNode: Node<BaseNodeData>,
	changedNodeOldHeight: number,
	changedNodeNewHeight: number,
	affectedNodes: Node<BaseNodeData>[],
	_allNodes: Node<BaseNodeData>[],
	_allEdges: Edge[]
): Map<string, { x: number; y: number }> {
	const positionMap = new Map<string, { x: number; y: number }>();

	// Calculate the dimension difference with extra padding to prevent overlap
	const dimensionDelta = changedNodeNewHeight - changedNodeOldHeight;

	// If the node expanded, we need to push downstream nodes
	// If it collapsed, we can pull them back
	if (Math.abs(dimensionDelta) < 10) {
		// No significant change, return empty map
		return positionMap;
	}

	// Add extra padding between nodes to prevent overlap
	const adjustedDelta = dimensionDelta + (dimensionDelta > 0 ? 100 : -100);

	// For vertical layout, adjust Y positions
	for (const node of affectedNodes) {
		// Only adjust nodes that are below the changed node
		if (node.position.y > changedNode.position.y) {
			const newY = node.position.y + adjustedDelta;
			positionMap.set(node.id, {
				x: node.position.x,
				y: newY,
			});
		}
	}

	return positionMap;
}

/**
 * Adjusts layout for switch node branches when the switch node expands
 */
export function adjustSwitchNodeBranchLayout(
	switchNode: Node<BaseNodeData>,
	switchNodeNewHeight: number,
	allNodes: Node<BaseNodeData>[],
	allEdges: Edge[]
): Map<string, { x: number; y: number }> {
	const positionMap = new Map<string, { x: number; y: number }>();

	// Find all edges coming from the switch node
	const switchEdges = allEdges.filter(edge => edge.source === switchNode.id && edge.type === 'branch');

	if (switchEdges.length === 0) {
		return positionMap;
	}

	// Find all child nodes of the switch node
	const childNodeIds = new Set(switchEdges.map(edge => edge.target));
	const childNodes = allNodes.filter(node => childNodeIds.has(node.id));

	// Calculate the new start position for branches (vertical layout)
	// Branches should start below the expanded switch node
	const branchStartY = switchNode.position.y + switchNodeNewHeight + FLOW_VERTICAL_SPACING;

	// Get the current branch layout
	const currentBranchYs = childNodes.map(node => node.position.y);
	const minCurrentY = Math.min(...currentBranchYs, branchStartY);

	// Calculate the offset needed
	const yOffset = branchStartY - minCurrentY;

	// Adjust all child nodes and their descendants
	const nodesToAdjust = new Set<string>();

	for (const childNode of childNodes) {
		nodesToAdjust.add(childNode.id);

		// Find all descendants of this child node
		const descendants = findAffectedNodes(childNode.id, allNodes, allEdges);
		for (const desc of descendants) {
			nodesToAdjust.add(desc.id);
		}
	}

	// Apply the offset to all affected nodes
	for (const nodeId of nodesToAdjust) {
		const node = allNodes.find(n => n.id === nodeId);
		if (node && node.position.y >= minCurrentY) {
			positionMap.set(nodeId, {
				x: node.position.x,
				y: node.position.y + yOffset,
			});
		}
	}

	return positionMap;
}

/**
 * Main function to adjust layout when a node expands or collapses
 */
export function adjustLayoutOnNodeChange(
	changedNodeId: string,
	changedNodeIsExpanded: boolean,
	allNodes: Node<BaseNodeData>[],
	allEdges: Edge[]
): Node<BaseNodeData>[] {
	const changedNode = allNodes.find(n => n.id === changedNodeId);
	if (!changedNode) {
		return allNodes;
	}

	// Estimate old and new dimensions
	const oldDimensions = estimateNodeDimensions({
		...changedNode,
		data: {
			...changedNode.data,
			isExpanded: !changedNodeIsExpanded,
		},
	});

	const newDimensions = estimateNodeDimensions({
		...changedNode,
		data: {
			...changedNode.data,
			isExpanded: changedNodeIsExpanded,
		},
	});

	// Check if this is a switch node
	const nodeData = changedNode.data;
	const isSwitchNode =
		'automationStep' in nodeData &&
		nodeData.automationStep !== null &&
		nodeData.automationStep !== undefined &&
		typeof nodeData.automationStep === 'object' &&
		'_type' in nodeData.automationStep &&
		nodeData.automationStep._type === 'SwitchAutomationStep';

	let positionMap: Map<string, { x: number; y: number }>;

	if (isSwitchNode) {
		// Special handling for switch nodes
		positionMap = adjustSwitchNodeBranchLayout(changedNode, newDimensions.height, allNodes, allEdges);
	} else {
		// Regular node adjustment
		const affectedNodes = findAffectedNodes(changedNodeId, allNodes, allEdges);
		positionMap = calculateAdjustedPositions(
			changedNode,
			oldDimensions.height,
			newDimensions.height,
			affectedNodes,
			allNodes,
			allEdges
		);
	}

	// Apply position changes
	if (positionMap.size === 0) {
		// Even if no position changes, update the changed node with new expansion state
		return allNodes.map(node => {
			if (node.id === changedNodeId) {
				return {
					...node,
					data: {
						...node.data,
						isExpanded: changedNodeIsExpanded,
						estimatedHeight: newDimensions.height,
						// Add transition flag for smooth animations
						shouldTransition: true,
					},
				};
			}
			return node;
		});
	}

	// Apply position changes and update node data
	return allNodes.map(node => {
		const newPosition = positionMap.get(node.id);
		if (newPosition) {
			return {
				...node,
				position: newPosition,
				data: {
					...node.data,
					// Enable smooth transitions for repositioned nodes
					shouldTransition: true,
				},
			};
		}
		// Update changed node with new state
		if (node.id === changedNodeId) {
			return {
				...node,
				data: {
					...node.data,
					isExpanded: changedNodeIsExpanded,
					estimatedHeight: newDimensions.height,
					shouldTransition: true,
				},
			};
		}
		return node;
	});
}
